import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Loader2, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi } from '@/services/api';
import { useToast } from '@/components/ui/toast';

type Step = 'email' | 'otp' | 'done';

export function ForgotPasswordPage() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { addToast } = useToast();

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      addToast({ title: '请输入注册邮箱', variant: 'warning' });
      return;
    }
    setIsLoading(true);
    try {
      const res = await authApi.forgotPassword(email.trim());
      if (res.data.userId) setUserId(res.data.userId);
      setStep('otp');
    } catch (error: any) {
      const msg = error.response?.data?.message || '请求失败，请稍后重试';
      addToast({ title: '发送失败', description: msg, variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      addToast({ title: '请输入 6 位验证码', variant: 'warning' });
      return;
    }
    if (!newPassword) {
      addToast({ title: '请输入新密码', variant: 'warning' });
      return;
    }
    if (newPassword !== confirmPassword) {
      addToast({ title: '两次密码输入不一致', variant: 'warning' });
      return;
    }
    if (!userId) {
      addToast({ title: '请重新输入邮箱获取验证码', variant: 'error' });
      setStep('email');
      return;
    }
    setIsLoading(true);
    try {
      await authApi.resetPassword({ userId, code: otpCode, newPassword });
      setStep('done');
    } catch (error: any) {
      const msg = error.response?.data?.message || '重置失败，请检查验证码是否正确';
      addToast({ title: '重置失败', description: msg, variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Link to="/login" className="text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <h2 className="text-xl font-semibold text-gray-900">重置密码</h2>
      </div>

      {step === 'email' && (
        <form onSubmit={handleRequestOtp} className="space-y-4">
          <p className="text-sm text-gray-500">请输入您的注册邮箱，我们将发送验证码用于重置密码。</p>
          <div className="space-y-2">
            <Label htmlFor="email">注册邮箱</Label>
            <Input
              id="email"
              type="email"
              placeholder="请输入注册邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            发送验证码
          </Button>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <p className="text-sm text-gray-500">
            验证码已发送至 <span className="font-medium">{email}</span>，请在 10 分钟内使用。
          </p>

          <div className="space-y-2">
            <Label htmlFor="otpCode">6 位验证码</Label>
            <Input
              id="otpCode"
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              className="tracking-widest text-center text-lg"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">新密码</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder="请输入新密码"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">确认新密码</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="请再次输入新密码"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || otpCode.length !== 6 || !newPassword}>
            {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            确认重置密码
          </Button>

          <button
            type="button"
            onClick={() => { setStep('email'); setOtpCode(''); }}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            重新获取验证码
          </button>
        </form>
      )}

      {step === 'done' && (
        <div className="text-center space-y-4 py-4">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">密码已重置</h3>
          <p className="text-gray-600 text-sm">请使用新密码登录您的账号。</p>
          <Link to="/login" className="block text-blue-600 hover:text-blue-700 text-sm font-medium pt-2">
            前往登录
          </Link>
        </div>
      )}
    </div>
  );
}
